// Lote 29 — máscara de data/hora em campo de texto comum, substituindo os campos nativos
// <input type="date"> / <input type="time">.
//
// Motivo: esses campos nativos guardam o valor digitado numa "caixinha" interna do
// próprio navegador, separada do que a página vê. Quando a página também controla o
// valor a cada tecla digitada (como o React faz), essa caixinha interna pode ficar
// dessincronizada — o campo MOSTRA a data certa na tela, mas o valor real que o
// navegador guarda fica vazio. Foi exatamente isso que a mensagem de diagnóstico do
// Lote 28 revelou: campo mostrando "13/10/2026" na tela, mas lido como data="" .
// Um campo de texto comum não tem essa caixinha escondida — o que aparece na tela é
// sempre, garantidamente, o que a página vê. Isso resolve o bug de vez, em vez de
// tentar mais uma correção em cima do campo nativo (o que já foi tentado nos Lotes
// 11, 16, 26 e 27 sem sucesso definitivo).

// Formata o que o usuário digita como "dd/mm/aaaa", conforme ele vai teclando números.
export function maskDateInput(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  let out = day;
  if (month) out += `/${month}`;
  if (year) out += `/${year}`;
  return out;
}

// Formata o que o usuário digita como "hh:mm".
export function maskTimeInput(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 4);
  const hour = digits.slice(0, 2);
  const minute = digits.slice(2, 4);
  let out = hour;
  if (minute) out += `:${minute}`;
  return out;
}

// "dd/mm/aaaa" -> "aaaa-mm-dd" (formato que o backend espera). Retorna "" se a data
// estiver incompleta ou não existir de verdade (ex.: 31/02).
export function dateBrToIso(br) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(br || "").trim());
  if (!m) return "";
  const [, d, mo, y] = m;
  const day = Number(d);
  const month = Number(mo);
  const year = Number(y);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return "";
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return "";
  return `${y}-${mo}-${d}`;
}

// "aaaa-mm-dd" (o que vem do backend) -> "dd/mm/aaaa" (o que mostramos no campo).
export function isoToDateBr(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!m) return "";
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
}

// "dd/mm/aaaa" -> objeto Date (horário local, não UTC — evita o clássico off-by-one-day
// perto da virada do dia em fusos negativos como o do Brasil). Usado pelo calendário
// clicável do campo Data (Lote 57, pedido do Robson: clicar no campo abre um calendário
// pra só clicar no dia em vez de digitar). Retorna undefined se a data não existir de
// verdade — mesma validação de dateBrToIso.
export function dateBrToJsDate(br) {
  const iso = dateBrToIso(br);
  if (!iso) return undefined;
  const [y, mo, d] = iso.split("-").map(Number);
  return new Date(y, mo - 1, d);
}

// Objeto Date -> "dd/mm/aaaa", o inverso de dateBrToJsDate — usado quando o usuário clica
// um dia no calendário, pra preencher o mesmo campo de texto mascarado de sempre (o
// calendário só clica, não substitui o campo por um input nativo — ver motivo detalhado
// no topo deste arquivo).
export function jsDateToDateBr(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

// Valida "hh:mm" (0-23h, 0-59min) — é o próprio formato que o backend já usa.
export function isValidTime(hhmm) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(String(hhmm || "").trim());
}

// Formata o número do processo no padrão CNJ conforme o usuário digita — NNNNNNN-DD.AAAA.J.TR.OOOO
// (Res. CNJ 65/2008, 20 dígitos: 7 do número sequencial, 2 do dígito verificador, 4 do ano,
// 1 do segmento de justiça, 2 do tribunal, 4 da unidade/vara de origem). Pedido do Robson,
// 30/09/2026: padronizar o campo "Processo" em Novo evento nesse formato, com as separações
// aplicadas automaticamente. Só formata quando o texto é puramente numérico (ou já vem com
// as próprias separações do CNJ, que são removidas e reaplicadas) — um processo antigo, de
// outro padrão de numeração, digitado com letras ou fora do padrão de 20 dígitos, passa
// direto sem máscara, pra não atrapalhar quem ainda cadastra processos assim.
export function maskCaseNumberInput(raw) {
  const text = String(raw || "");
  // Se tem alguma letra, não é um número CNJ — não mascara (ex.: processos administrativos,
  // sindicâncias, numeração antiga com prefixo de letra).
  if (/[a-zA-Z]/.test(text)) return text;
  const digits = text.replace(/\D/g, "").slice(0, 20);
  if (!digits) return "";
  const seq = digits.slice(0, 7);
  const dv = digits.slice(7, 9);
  const year = digits.slice(9, 13);
  const segment = digits.slice(13, 14);
  const tribunal = digits.slice(14, 16);
  const origin = digits.slice(16, 20);
  let out = seq;
  if (dv) out += `-${dv}`;
  if (year) out += `.${year}`;
  if (segment) out += `.${segment}`;
  if (tribunal) out += `.${tribunal}`;
  if (origin) out += `.${origin}`;
  return out;
}

// Confere o dígito verificador do número CNJ (Res. CNJ 65/2008, módulo 97 base 10): o número sem o DV, com "00"
// no lugar dele, deixa resto r; o DV certo é 98 - r. Devolve true/false, ou null quando ainda não tem 20 dígitos
// (ou tem letras): nesse caso não há o que conferir. Só serve para um aviso — nunca bloqueia o cadastro.
export function cnjDvValido(raw) {
  const text = String(raw || "");
  if (/[a-zA-Z]/.test(text)) return null;
  const d = text.replace(/\D/g, "");
  if (d.length !== 20) return null;
  const base = d.slice(0, 7) + d.slice(9, 20) + "00";   // NNNNNNN AAAA J TR OOOO 00
  let r = 0;
  for (const ch of base) r = (r * 10 + Number(ch)) % 97;
  return String(98 - r).padStart(2, "0") === d.slice(7, 9);
}

// Formata valor em reais conforme o usuário digita, tratando os dígitos como centavos (de
// trás pra frente, como em caixa eletrônico/maquininha) — "500" vira "R$ 5,00", "50000" vira
// "R$ 500,00". Lote novo (02/10/2026, pedido do Robson): campo "Valor da causa" ganhou essa
// máscara (antes era texto livre com um placeholder "R$ 0,00" que não fazia nada). O valor
// guardado é o próprio texto formatado — valor_causa é string livre no backend, não há
// conversão de volta pra número.
export function maskCurrencyInput(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  const cents = parseInt(digits, 10);
  if (Number.isNaN(cents)) return "";
  const reais = Math.floor(cents / 100);
  const centavos = cents % 100;
  const reaisStr = String(reais).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `R$ ${reaisStr},${String(centavos).padStart(2, "0")}`;
}

// Formata telefone brasileiro conforme o usuário digita: "(XX) XXXXX-XXXX" pra celular
// (9 dígitos após o DDD) ou "(XX) XXXX-XXXX" pra fixo (8 dígitos) — decide sozinho pelo
// número de dígitos já digitados, sem precisar de um seletor separado "fixo/celular".
export function maskPhoneInput(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 11);
  if (!digits) return "";
  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);
  if (!rest) return `(${ddd}`;
  if (ddd.length < 2) return ddd;
  // 11 dígitos totais (DDD + 9) é celular; 10 ou menos é fixo (DDD + 8).
  const isMobile = digits.length > 10;
  const splitAt = isMobile ? 5 : 4;
  const part1 = rest.slice(0, splitAt);
  const part2 = rest.slice(splitAt);
  let out = `(${ddd}) ${part1}`;
  if (part2) out += `-${part2}`;
  return out;
}
